import React from 'react';
import debounce from 'lodash/debounce';
import { isTimeStamp } from '@/utils/common-utils';
import { getCookie } from '@/utils/storage';
import GlobalContext from '@/context/globalContext';
import { NOT_FOUND_PAGE_ID } from '@/constants/component-id';
import { sendEventTracker } from '@/api/tanzhi';

export const sendEvent = (eventData: {
  name: string;
  [key: string]:
    | string
    | number
    | Record<string, string | number | null>[]
    | null
    | undefined;
}) => {
  try {
    const isNotFoundPage = document.querySelector(
      `[data-id="${NOT_FOUND_PAGE_ID}"]`,
    );
    if (isNotFoundPage) {
      return;
    }
    const { name } = eventData;
    if (!name) {
      return;
    }
    const _bi = getCookie('_bi');
    const isValidDate = _bi && isTimeStamp(_bi);
    if (!isValidDate && name === 'impression') {
      return;
    }
    if (isValidDate) {
      eventData._bi = _bi;
    }
    const referer = GlobalContext.getContext('referer');
    if (referer) {
      eventData.referrer_url = referer;
    }
    const cid = sessionStorage.getItem('cid');
    const sid = sessionStorage.getItem('sid');
    sendEventTracker(
      eventData,
      cid && sid
        ? {
            headers: {
              'X-Session-Id': sid,
              'X-Client-Id': cid,
            },
          }
        : undefined,
    )
      .then()
      .catch((err) => console.error('Error:', err));
  } catch (error) {
    console.error('Error:', error);
  }
};

export const trackCustomEvent = (name: string, ele: Element) => {
  if (!name) {
    return;
  }
  const eleId = ele.getAttribute('data-ele-id');
  const eleGroup = ele.getAttribute('data-ele-group');
  if (!eleId || !eleGroup) {
    return;
  }
  sendEvent({
    name,
    ele_id: eleId,
    ele_group: eleGroup,
  });
};

const handleClickEvent = debounce((e) => {
  const ele = e.target as HTMLElement;
  if (!ele.getAttribute('data-ele-id')) {
    const parentEle = ele.closest('[data-ele-id]');
    parentEle && trackCustomEvent('click', parentEle);
  } else {
    trackCustomEvent('click', ele);
  }
}, 300);

export const trackClickEvent = (
  e:
    | React.MouseEvent<
        | HTMLAnchorElement
        | HTMLButtonElement
        | HTMLDivElement
        | HTMLLIElement
        | SVGSVGElement
      >
    | Event
    | React.SyntheticEvent,
  stopPropagation = false,
) => {
  if (stopPropagation) {
    e.stopPropagation();
  }
  handleClickEvent(e);
};

export const useIntersectionObserver = (
  observerElements: (Element | null)[],
) => {
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        let elements: Element[] = [];
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            elements.push(entry.target);
            observer.unobserve(entry.target);
          }
        });
        if (elements.length > 0) {
          sendEvent({
            name: 'impression',
            ele_info: elements.map((ele) => ({
              ele_id: ele.getAttribute('data-ele-id'),
              ele_group: ele.getAttribute('data-ele-group'),
            })),
          });
          elements = [];
        }
      },
      { threshold: 0.5 },
    );
    observerElements.forEach((element) => element && observer.observe(element));
    return () =>
      observerElements.forEach(
        (element) => element && observer.unobserve(element),
      );
  }, [observerElements]);
};
